import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { JEPX_AREA_CODES } from '~/models/area'

/**
 * 札を作るモーダル (個別作成 / コマクリックの札作成 / ブロック作成) で選べるエリア
 * (AC-BSP-123 の「今は選べないエリアは空欄」 の判定)。 [[isAreaInCandidates]] と
 * [[isAreaTradable]] の両方を満たすエリア。 業務エリア外は選択肢には残る
 * (AC-BSP-005b。 `useSelectableAreas`) が、 ここには含めない。
 *
 * - `candidateAreaCodes` は業務エリア設定で絞った候補 (AC-BSP-019、
 *   `~/hooks/useBusinessAreaFilter`)
 * - 取引不可エリア (`disabledAreas`) は未取得 (undefined) の間は制限しない
 * - `keepAreaCodes` は取引不可・業務エリア外でも残す (編集中の札のエリアが、 開いた
 *   瞬間に別エリアへ化けるのを防ぐ)。 ブロック入札の LINK / LOOP は 2 leg 分を渡す。
 *   候補・取引可否の判定と同じ keep を使うため、 3 つとも省略できない引数にしている
 */
export function selectableAreaCodes(
  disabledAreas: ReadonlySet<AreaCode> | undefined,
  candidateAreaCodes: readonly AreaCode[],
  keepAreaCodes: readonly AreaCode[],
): AreaCode[] {
  return JEPX_AREA_CODES.filter(
    (code) =>
      isAreaInCandidates(code, candidateAreaCodes, keepAreaCodes) &&
      isAreaTradable(code, disabledAreas, keepAreaCodes),
  )
}

/**
 * 業務エリアの候補 (`candidateAreaCodes`) に入るか、 残す指定 (`keepAreaCodes`) のエリアか。
 * 選択肢の組み立てと、 選択中のエリアが候補外になった時の選択解除で使う (どちらも
 * [[useSelectableAreas]] が呼ぶ)。
 */
export function isAreaInCandidates(
  areaCode: AreaCode,
  candidateAreaCodes: readonly AreaCode[],
  keepAreaCodes: readonly AreaCode[],
): boolean {
  return (
    keepAreaCodes.includes(areaCode) || candidateAreaCodes.includes(areaCode)
  )
}

/**
 * 取引可能エリアか (`keepAreaCodes` のエリアは取引不可でも通す)。 送信直前の再照合にも
 * 使う: 取引可能エリアの取得完了前に選んだ値は、 後から選択肢から消えても form に残る
 * ため、 選択肢だけに頼らず送信時にも照合する。 業務エリアの候補外になった値は
 * [[useSelectableAreas]] が選択を解除する ([[isAreaInCandidates]]) ので、 送信直前の
 * 照合は取引可否だけを見る。
 */
export function isAreaTradable(
  areaCode: AreaCode,
  disabledAreas: ReadonlySet<AreaCode> | undefined,
  keepAreaCodes: readonly AreaCode[],
): boolean {
  return (
    keepAreaCodes.includes(areaCode) || !(disabledAreas?.has(areaCode) ?? false)
  )
}
